import { useEffect, useRef, useState, type ReactNode } from 'react';
import { haptic } from '../lib/native';
import { useT } from '../i18n';

const TRIGGER = 70; // px of pull (after resistance) that starts a refresh
const MAX = 110;

/**
 * Pull down from the top of the page to refresh, like a native list. Touch only; the page itself scrolls (window).
 * The spinner sits under the status bar and the content follows the finger with some resistance.
 */
export function PullToRefresh({ onRefresh, children }: { onRefresh: () => Promise<unknown>; children: ReactNode }) {
  const t = useT();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<number | null>(null);
  const armed = useRef(false);
  const pullRef = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      if (busy.current || window.scrollY > 0 || e.touches.length !== 1) return;
      start.current = e.touches[0]!.clientY;
      armed.current = false;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const dy = e.touches[0]!.clientY - start.current;
      if (dy <= 0 || window.scrollY > 0) {
        pullRef.current = 0;
        setPull(0);
        return;
      }
      const d = Math.min(MAX, dy * 0.5);
      if (d >= TRIGGER && !armed.current) {
        armed.current = true;
        haptic();
      } else if (d < TRIGGER) armed.current = false;
      pullRef.current = d;
      setPull(d);
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      if (pullRef.current >= TRIGGER) {
        busy.current = true;
        setRefreshing(true);
        setPull(TRIGGER * 0.75);
        void onRefresh().finally(() => {
          busy.current = false;
          setRefreshing(false);
          setPull(0);
        });
      } else setPull(0);
      pullRef.current = 0;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh]);

  const settling = start.current === null;
  const progress = Math.min(1, pull / TRIGGER);
  return (
    <>
      <div
        className="ptr"
        aria-hidden={!refreshing}
        role="status"
        style={{ opacity: refreshing ? 1 : progress, transform: `translate(-50%, ${pull - 40}px)`, transition: settling ? 'transform 200ms ease, opacity 200ms ease' : 'none' }}
      >
        <span className={refreshing ? 'ptr-spin' : undefined} style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }} />
        {refreshing && <span className="sr-only">{t.common.refreshing}</span>}
      </div>
      <div style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: settling ? 'transform 200ms ease' : 'none' }}>{children}</div>
    </>
  );
}
